import React, { useEffect, useRef, useState } from 'react';
import { verifyStatus } from '../services/api';
import { PaperAirplaneIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

export interface VerifyPayload {
  requiresTelegramVerification: true;
  verifyCode: string;
  botUsername: string;
  verifyDeepLink: string;
  email: string;
}

/**
 * "Confirm your account in Telegram" gate. Shown after registration (or on
 * a login attempt by an unverified operator). Renders the one-tap deep link
 * to the bot and polls verify-status until the bot links the account, then
 * calls onVerified().
 */
const TelegramVerifyPanel: React.FC<{
  payload: VerifyPayload;
  onVerified: () => void;
}> = ({ payload, onVerified }) => {
  const [checking, setChecking] = useState(false);
  const [expired, setExpired] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const check = async () => {
    setChecking(true);
    try {
      const res = await verifyStatus(payload.verifyCode);
      const data = res.data?.data || {};
      if (data.verified) {
        if (pollRef.current) clearInterval(pollRef.current);
        onVerified();
      } else if (data.expired) {
        setExpired(true);
      }
    } catch {
      /* keep polling — transient errors are fine */
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    // Poll every 3s — the operator typically taps Start within seconds.
    pollRef.current = setInterval(check, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload.verifyCode]);

  const hasLink = !!payload.verifyDeepLink && !!payload.botUsername;

  return (
    <div className="space-y-5 text-center">
      <div className="mx-auto h-14 w-14 flex items-center justify-center rounded-full bg-sky-500/10">
        <PaperAirplaneIcon className="h-7 w-7 text-sky-500 -rotate-45" />
      </div>
      <div>
        <h2 className="text-xl font-bold text-foreground">Подтвердите аккаунт в Telegram</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Аккаунт <span className="font-medium text-foreground">{payload.email}</span> создан.
          Чтобы войти, подтвердите его через нашего бота — это займёт пару секунд.
        </p>
      </div>

      {hasLink ? (
        <a
          href={payload.verifyDeepLink}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-sky-500 text-white font-medium hover:bg-sky-600 transition-colors"
        >
          <PaperAirplaneIcon className="h-5 w-5 -rotate-45" />
          Открыть бота и подтвердить
        </a>
      ) : (
        <div className="text-sm text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-md p-3">
          Бот ещё не настроен администратором. Обратитесь к суперадмину, чтобы он
          указал токен бота в настройках.
        </div>
      )}

      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        {checking ? (
          <>
            <span className="h-2 w-2 rounded-full bg-sky-400 animate-pulse" />
            Проверяем подтверждение…
          </>
        ) : (
          <>Ожидаем, пока вы нажмёте Start в боте…</>
        )}
      </div>

      {expired && (
        <p className="text-xs text-rose-500">
          Срок действия ссылки истёк. Попробуйте войти заново — будет выдана новая ссылка.
        </p>
      )}

      <button
        type="button"
        onClick={check}
        className="text-xs text-primary hover:underline"
      >
        Я уже подтвердил — проверить сейчас
      </button>
    </div>
  );
};

export { TelegramVerifyPanel };
export default TelegramVerifyPanel;
